import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { HiDownload, HiPencil, HiPlus, HiTrash, HiUpload } from 'react-icons/hi';
import { PageLayout, ActionButton } from '../../components/modern';
import { adminApi } from '../../utils/api';
import { getApiUrl } from '../../config/env';
import { showErrorToast, showSuccessToast } from '../../utils/toastNotifications';
import './TaxProfiles.scss';

type Category = { id: number; name: string };
type Company = { id: number; name: string };
type Rule = { categoryId: number; categoryName?: string; value: number; type: number };
type Profile = { id: number; name: string; categoryTaxes: Rule[]; shippingCompanyIds: number[] };

const emptyRules = (categories: Category[]): Rule[] => categories.map(c => ({ categoryId: c.id, categoryName: c.name, value: 0, type: 0 }));

export const TaxProfiles: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const [categoryResult, companyResult, profileResult] = await Promise.all([
      adminApi.call('/category?page=1&pageSize=1000', 'GET'),
      adminApi.call('/shippingcompany?page=1&pageSize=1000', 'GET'),
      adminApi.call<Profile[]>('/taxprofile', 'GET')
    ]);
    if (!categoryResult.success || !companyResult.success || !profileResult.success) {
      showErrorToast('Could not load tax profiles.');
      return;
    }
    const loadedCategories = (categoryResult.data as any)?.items || [];
    setCategories(loadedCategories);
    setCompanies((companyResult.data as any)?.items || []);
    setProfiles(profileResult.data || []);
    if (editingId === null) setRules(emptyRules(loadedCategories));
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditingId(null); setName(''); setCompanyIds([]); setRules(emptyRules(categories));
  };

  const edit = (profile: Profile) => {
    const byCategory = new Map(profile.categoryTaxes.map(r => [r.categoryId, r]));
    setEditingId(profile.id); setName(profile.name); setCompanyIds(profile.shippingCompanyIds);
    setRules(categories.map(c => byCategory.get(c.id) || { categoryId: c.id, categoryName: c.name, value: 0, type: 0 }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async () => {
    if (!name.trim()) { showErrorToast('Profile name is required.'); return; }
    setSaving(true);
    const body = { id: editingId || 0, name: name.trim(), categoryTaxes: rules, shippingCompanyIds: companyIds };
    const result = editingId
      ? await adminApi.call(`/taxprofile/${editingId}`, 'PUT', body)
      : await adminApi.call('/taxprofile', 'POST', body);
    setSaving(false);
    if (!result.success) { showErrorToast(result.error || 'Could not save tax profile.'); return; }
    showSuccessToast(editingId ? 'Tax profile updated.' : 'Tax profile created.');
    reset(); await load();
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this tax profile? Shipping companies will be unassigned.')) return;
    const result = await adminApi.call(`/taxprofile/${id}`, 'DELETE');
    if (!result.success) { showErrorToast(result.error || 'Could not delete tax profile.'); return; }
    showSuccessToast('Tax profile deleted.'); await load();
  };

  const downloadTemplate = async () => {
    const response = await fetch(getApiUrl('/taxprofile/template'), { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
    if (!response.ok) { showErrorToast('Could not download the template.'); return; }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a'); link.href = url; link.download = 'tax-profiles-template.xlsx'; link.click(); URL.revokeObjectURL(url);
  };

  const importExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append('file', file);
    const result = await adminApi.upload<{ imported: number }>('/taxprofile/import', form);
    event.target.value = '';
    if (!result.success) { showErrorToast(result.error || 'Import failed.'); return; }
    showSuccessToast(`${result.data?.imported || 0} tax profile(s) imported.`); reset(); await load();
  };

  const companyOptions = companies.map(c => ({ value: c.id, label: c.name }));
  return (
    <PageLayout title="Tax Profiles" subtitle="Define a percent or fixed tax for every category and assign the profile to shipping companies." breadcrumbs={[{ label: 'Tax Profiles' }]}>
      <div className="tax-profile-card">
        <div className="tax-profile-heading"><h5>{editingId ? 'Edit Tax Profile' : 'New Tax Profile'}</h5><div className="tax-profile-actions">
          <ActionButton variant="secondary" icon={HiDownload} onClick={downloadTemplate}>Excel Template</ActionButton>
          <ActionButton variant="secondary" icon={HiUpload} onClick={() => fileRef.current?.click()}>Import Excel</ActionButton>
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={importExcel} />
        </div></div>
        <div className="form-group"><label className="form-label">Name <span className="required">*</span></label><input className="form-control" value={name} onChange={e => setName(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Shipping Companies</label>
          <Select isMulti options={companyOptions} value={companyOptions.filter(x => companyIds.includes(x.value))} onChange={values => setCompanyIds(values.map(x => x.value))} placeholder="Select one or more shipping companies" />
        </div>
        <div className="tax-rule-table"><table className="table table-bordered"><thead><tr><th>Category</th><th>Tax</th><th>Type</th></tr></thead><tbody>
          {rules.map((rule, index) => <tr key={rule.categoryId}><td>{rule.categoryName || categories.find(c => c.id === rule.categoryId)?.name}</td><td><input type="number" min="0" max={rule.type === 0 ? 100 : undefined} step="0.01" className="form-control" value={rule.value} onChange={e => setRules(old => old.map((x, i) => i === index ? { ...x, value: Number(e.target.value) } : x))} /></td><td><select className="form-select" value={rule.type} onChange={e => setRules(old => old.map((x, i) => i === index ? { ...x, type: Number(e.target.value) } : x))}><option value={0}>Percent (%)</option><option value={1}>Fixed value</option></select></td></tr>)}
        </tbody></table></div>
        <div className="tax-profile-actions"><ActionButton variant="primary" icon={editingId ? HiPencil : HiPlus} onClick={save} loading={saving}>{editingId ? 'Update Profile' : 'Create Profile'}</ActionButton>{editingId && <ActionButton variant="secondary" onClick={reset}>Cancel</ActionButton>}</div>
      </div>
      <div className="tax-profile-card"><h5>Existing Tax Profiles</h5><div className="table-responsive"><table className="table table-striped"><thead><tr><th>Name</th><th>Shipping Companies</th><th>Rules</th><th>Actions</th></tr></thead><tbody>
        {profiles.map(profile => <tr key={profile.id}><td>{profile.name}</td><td>{profile.shippingCompanyIds.map(id => companies.find(c => c.id === id)?.name).filter(Boolean).join(', ') || '—'}</td><td>{profile.categoryTaxes.length}</td><td><div className="tax-profile-actions"><ActionButton variant="primary" size="sm" icon={HiPencil} onClick={() => edit(profile)} /><ActionButton variant="danger" size="sm" icon={HiTrash} onClick={() => remove(profile.id)} /></div></td></tr>)}
        {!profiles.length && <tr><td colSpan={4} className="text-center">No tax profiles yet.</td></tr>}
      </tbody></table></div></div>
    </PageLayout>
  );
};
